import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import auctionService from "../services/auctionService";
import defaultLogo from "../assets/5bc.png";

interface PopupContent {
  title: string;
  description: string;
  button_text: string;
  logo_url: string;
}

const NewsCard: React.FC = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [content, setContent] = useState<PopupContent>({
    title: "Florida Gateway Auction",
    description:
      "Welcome to our P2P Currency Exchange! Here you can safely buy and sell currency with other users.",
    button_text: "More...",
    logo_url: defaultLogo,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [showFullText, setShowFullText] = useState(false);

  useEffect(() => {
    const fetchPopupContent = async () => {
      try {
        const data = await auctionService.getPopupContentPublic(i18n.language);
        // Normalize logo_url: if it's /5bc.png or /static/5bc.png, use the imported logo instead
        const normalizedLogoUrl =
          !data.logo_url ||
          data.logo_url === "/5bc.png" ||
          data.logo_url === "/static/5bc.png" ||
          data.logo_url.endsWith("5bc.png")
            ? defaultLogo
            : data.logo_url;
        setContent({
          ...data,
          logo_url: normalizedLogoUrl,
        });
      } catch (err) {
        console.error("Failed to load popup content:", err);
        setError(true);
        // Fallback to default content
      } finally {
        setLoading(false);
      }
    };

    fetchPopupContent();
  }, [i18n.language]);

  if (loading) {
    return (
      <Card sx={{ maxWidth: 1200, margin: "0 auto", mt: 4 }}>
        <CardContent sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  // Function to truncate text to approximately 5 lines
  const truncateText = (text: string, maxLines: number = 5) => {
    const words = text.split(" ");
    const wordsPerLine = 15; // Approximate words per line
    const maxWords = wordsPerLine * maxLines;

    if (words.length <= maxWords) {
      return text;
    }

    return words.slice(0, maxWords).join(" ") + "...";
  };

  const displayText = showFullText
    ? content.description
    : truncateText(content.description);

  return (
    <Card sx={{ maxWidth: 1200, margin: "0 auto", mt: 4 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <img
            src={defaultLogo}
            alt="Logo"
            style={{ height: 48, marginRight: 16 }}
            onError={(e) => {
              // Prevent any error loops - just use defaultLogo
              const target = e.target as HTMLImageElement;
              if (!target.src.includes("5bc.5980afe3d34e94b9a4d3.png") && !target.src.includes("/static/media/5bc.")) {
                target.src = defaultLogo;
              }
            }}
          />
          <Typography variant="h5">{content.title}</Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            mb: 2,
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
          }}
        >
          {displayText}
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          {!showFullText && content.description.length > 75 && (
            <Button
              variant="outlined"
              onClick={() => setShowFullText(true)}
              size="small"
            >
              More...
            </Button>
          )}
          {showFullText && (
            <Button
              variant="outlined"
              onClick={() => setShowFullText(false)}
              size="small"
            >
              Less
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => navigate("/about-currency")}
          >
            {content.button_text}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NewsCard;
